import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import "@/App.css";
import { Toaster, toast } from "sonner";
import {
    fetchStatus,
    fetchGrid,
    fetchOrders,
    fetchTrades,
    fetchPnl,
    fetchPriceHistory,
    fetchEvents,
    fetchConfig,
    startBot,
    stopBot,
    setMode,
    setNetwork,
    updateConfig,
    recover,
    EVENTS_STREAM_URL,
} from "@/lib/api";
import Header from "@/components/Header";
import GridLadder from "@/components/GridLadder";
import PriceChart from "@/components/PriceChart";
import StatsBento from "@/components/StatsBento";
import TerminalLog from "@/components/TerminalLog";
import OperationsPanel from "@/components/OperationsPanel";
import { OpenOrdersTable, TradeHistoryTable } from "@/components/OrdersTables";
import { ConfirmStopDialog, ConfirmLiveDialog, ConfirmNetworkDialog, ConfirmPaperDialog } from "@/components/ConfirmDialogs";
import SettingsDialog from "@/components/SettingsDialog";
import ExclusionsPanel from "@/components/ExclusionsPanel";
import GridDetailDialog from "@/components/GridDetailDialog";
import DocsPage from "@/components/DocsPage";
import SmartGridPage from "@/components/SmartGridPage";
import SmartGridDocsPage from "@/components/SmartGridDocsPage";
import HistoryPage from "@/components/HistoryPage";
import GroupSellPage from "@/components/GroupSellPage";
import LoginPage from "@/components/LoginPage";
import ResetPasswordPage from "@/components/ResetPasswordPage";
import ServerLoadPage from "@/components/ServerLoadPage";
import MultiGridPage from "@/components/MultiGridPage";
import GridDashboardPage from "@/components/GridDashboardPage";
import DataLabPage from "@/components/DataLabPage";
import SmartGridBlueprintPage from "@/components/SmartGridBlueprint";
import SmartGridArchitecturePage from "@/components/SmartGridArchitecturePage";
import SmartGridSystemMapPage from "@/components/SmartGridSystemMapPage";
import { authMe, authLogout } from "@/lib/auth_api";
import useTradeAlerts from "@/hooks/useTradeAlerts";
import { loadAlertPrefs } from "@/lib/tradeAlerts";
import {
    fetchSgHistoryTrades, fetchSgHistoryStats, fetchSgHistoryIntegrity, sgExportHistoryCsvUrl,
    fetchSgBotStatus, fetchSgGroupSellActive, previewSgGroupSell, executeSgGroupSell,
    fetchSgOrphans, resolveSgOrphansManual, fetchSgCombos, cancelSgCombo,
} from "@/lib/smartgrid_api";

const SG_HISTORY_API = {
    fetchHistoryTrades: fetchSgHistoryTrades,
    fetchHistoryStats: fetchSgHistoryStats,
    fetchHistoryIntegrity: fetchSgHistoryIntegrity,
    exportHistoryCsvUrl: sgExportHistoryCsvUrl,
};
const SG_GROUPSELL_API = {
    fetchStatus: fetchSgBotStatus,
    fetchGroupSellActive: fetchSgGroupSellActive,
    previewGroupSell: previewSgGroupSell,
    executeGroupSell: executeSgGroupSell,
    fetchOrphans: fetchSgOrphans,
    resolveOrphansManual: resolveSgOrphansManual,
    fetchCombos: fetchSgCombos,
    cancelCombo: cancelSgCombo,
};

function useInterval(fn, ms) {
    const ref = useRef(fn);
    ref.current = fn;
    useEffect(() => {
        const id = setInterval(() => ref.current(), ms);
        ref.current();
        return () => clearInterval(id);
    }, [ms]);
}

function Dashboard({ onLogout }) {
    const [status, setStatus] = useState(null);
    const [grid, setGrid] = useState(null);
    const [orders, setOrders] = useState([]);
    const [trades, setTrades] = useState([]);
    const [pnl, setPnl] = useState(null);
    const [priceHistory, setPriceHistory] = useState([]);
    const [events, setEvents] = useState([]);
    const [config, setConfig] = useState(null);

    const [stopOpen, setStopOpen] = useState(false);
    const [liveOpen, setLiveOpen] = useState(false);
    const [paperOpen, setPaperOpen] = useState(false);
    const [networkOpen, setNetworkOpen] = useState(false);
    const [networkTarget, setNetworkTarget] = useState("testnet");
    const [settingsOpen, setSettingsOpen] = useState(false);
    const [gridDetailId, setGridDetailId] = useState(null);

    // Buy/sell alert prefs held in a ref so live setting changes take effect
    // immediately without re-subscribing the trade-watch effect.
    const alertPrefsRef = useRef(loadAlertPrefs());
    useTradeAlerts(trades, alertPrefsRef);

    // Live status (every 1s)
    useInterval(async () => {
        try { setStatus(await fetchStatus()); } catch (_) { /* ignore */ }
    }, 1000);

    // Grid + price history (every 500ms for snappy ladder updates)
    useInterval(async () => {
        try {
            const [g, ph] = await Promise.all([fetchGrid(), fetchPriceHistory()]);
            setGrid(g);
            setPriceHistory(ph?.items || []);
        } catch (_) { /* ignore */ }
    }, 700);

    // Orders + trades + pnl (every 2s)
    useInterval(async () => {
        try {
            const [o, t, p] = await Promise.all([fetchOrders("open"), fetchTrades(200), fetchPnl()]);
            setOrders(o?.items || []);
            setTrades(t?.items || []);
            setPnl(p);
        } catch (_) { /* ignore */ }
    }, 2000);

    // Config on mount
    useEffect(() => {
        (async () => {
            try {
                const cfg = await fetchConfig();
                setConfig(cfg);
                // Initial events backfill
                const ev = await fetchEvents(150);
                if (ev?.items) setEvents(ev.items.slice().reverse());
            } catch (_) { /* ignore */ }
        })();
    }, []);

    // Live events via SSE
    useEffect(() => {
        let es;
        try {
            es = new EventSource(EVENTS_STREAM_URL, { withCredentials: true });
            es.onmessage = (e) => {
                if (!e.data) return;
                try {
                    const evt = JSON.parse(e.data);
                    if (!evt || !evt.ts_ms) return;
                    setEvents((prev) => {
                        const next = [...prev, evt];
                        if (next.length > 500) next.splice(0, next.length - 500);
                        return next;
                    });
                } catch (_) { /* ignore */ }
            };
            es.onerror = () => { /* let browser reconnect */ };
        } catch (_) { /* ignore */ }
        return () => { try { es && es.close(); } catch (_) { /* ignore */ } };
    }, []);

    const onStart = async () => {
        try {
            await startBot(status?.mode || "paper");
            toast.success(`Bot started in ${(status?.mode || "paper").toUpperCase()} mode`);
        } catch (e) {
            toast.error(`Start failed: ${e?.response?.data?.detail || e.message}`);
        }
    };
    const onStopRequest = () => setStopOpen(true);
    const onStopConfirmed = async () => {
        setStopOpen(false);
        try {
            await stopBot();
            toast("Bot stopped", { description: "Open orders preserved (live) / paper exchange retained." });
        } catch (e) {
            toast.error(`Stop failed: ${e.message}`);
        }
    };
    const onModeToggleRequest = async (next) => {
        if (next === "live") {
            setLiveOpen(true);
            return;
        }
        // Switching down to PAPER — confirm first.
        setPaperOpen(true);
    };
    const onPaperConfirmed = async () => {
        setPaperOpen(false);
        try {
            await setMode("paper");
            toast.success("Switched to PAPER mode");
        } catch (e) {
            toast.error(`Mode switch failed: ${e.message}`);
        }
    };
    const onLiveConfirmed = async () => {
        try {
            await setMode("live");
            setLiveOpen(false);
            toast.success("Switched to LIVE mode");
        } catch (e) {
            toast.error(`LIVE switch failed: ${e?.response?.data?.detail || e.message}`);
        }
    };
    const onNetworkToggleRequest = (target) => {
        if (status?.running) {
            toast.error("Stop the bot before switching network");
            return;
        }
        setNetworkTarget(target);
        setNetworkOpen(true);
    };
    const onNetworkConfirmed = async () => {
        setNetworkOpen(false);
        try {
            await setNetwork(networkTarget === "testnet");
            toast.success(`Trading network: ${networkTarget.toUpperCase()}`);
        } catch (e) {
            toast.error(`Network switch failed: ${e?.response?.data?.detail || e.message}`);
        }
    };
    const onForceRecover = async () => {
        try {
            const r = await recover();
            toast.success("Recovery triggered", { description: JSON.stringify(r) });
        } catch (e) {
            toast.error(`Recover failed: ${e.message}`);
        }
    };
    const onSaveSettings = async (cfg) => {
        try {
            await updateConfig(cfg);
            const refreshed = await fetchConfig();
            setConfig(refreshed);
            setSettingsOpen(false);
            toast.success("Settings saved");
        } catch (e) {
            toast.error(`Save failed: ${e?.response?.data?.detail || e.message}`);
        }
    };

    const buys = grid?.active_buys || [];
    const sells = grid?.active_sells || [];
    const price = status?.last_price || grid?.last_price || null;

    return (
        <div className="min-h-screen text-[#E6EDF6]">
            <Toaster
                position="bottom-right"
                toastOptions={{
                    style: { background: "#0F1620", border: "1px solid #1B2A3A", color: "#E6EDF6" },
                }}
            />
            <Header
                status={status}
                onStart={onStart}
                onStopRequest={onStopRequest}
                onModeToggleRequest={onModeToggleRequest}
                onNetworkToggleRequest={onNetworkToggleRequest}
                onOpenSettings={() => setSettingsOpen(true)}
                onForceRecover={onForceRecover}
                onLogout={onLogout}
            />

            <div className="hero-grad">
                <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 pt-5 pb-3">
                    <div className="grid grid-cols-12 gap-4 lg:gap-6">
                        {/* Hero: ladder */}
                        <div className="col-span-12 lg:col-span-7">
                            <GridLadder
                                price={price}
                                gridSize={status?.config?.grid_size || 250}
                                buys={buys}
                                sells={sells}
                                mode={status?.mode || "paper"}
                                symbol={status?.config?.symbol || grid?.symbol || "BTCUSDT"}
                            />
                        </div>
                        {/* Side stack */}
                        <div className="col-span-12 lg:col-span-5 flex flex-col gap-4">
                            <StatsBento status={status} pnl={pnl} />
                            <PriceChart data={priceHistory} buys={buys} sells={sells} symbol={status?.config?.symbol || "BTCUSDT"} />
                        </div>
                    </div>
                </div>
            </div>

            <div className="max-w-[1440px] mx-auto px-4 md:px-6 lg:px-8 py-4">
                <div className="grid grid-cols-12 gap-4 lg:gap-6">
                    {/* Pair switching removed from the main grid — it's locked to
                        BTCUSDT. Other pairs run as isolated grids under #/grids. */}
                    <div className="col-span-12">
                        <OperationsPanel status={status} onRefresh={async () => {
                            try { setStatus(await fetchStatus()); } catch (_) { /* ignore */ }
                        }} />
                    </div>
                    <div className="col-span-12">
                        <ExclusionsPanel currentPrice={price} onChange={async () => {
                            try { setGrid(await fetchGrid()); } catch (_) { /* ignore */ }
                        }} />
                    </div>
                    <div className="col-span-12 lg:col-span-6">
                        <OpenOrdersTable orders={orders} onGridClick={setGridDetailId} />
                    </div>
                    <div className="col-span-12 lg:col-span-6">
                        <TradeHistoryTable trades={trades} onGridClick={setGridDetailId} />
                    </div>
                    <div className="col-span-12">
                        <TerminalLog events={events} />
                    </div>
                </div>
                <div className="py-6 text-center text-[10px] uppercase tracking-[0.18em] text-[#3A4759]">
                    AsterDex Spot V3 · Dynamic Self-Healing Trailing Grid Bot ·
                    {" "}{status?.use_testnet ? "Trading: TESTNET" : "Trading: MAINNET"}
                    {" "}· Market data: MAINNET
                </div>
            </div>

            <ConfirmStopDialog open={stopOpen} onOpenChange={setStopOpen} onConfirm={onStopConfirmed} />
            <ConfirmLiveDialog
                open={liveOpen}
                onOpenChange={setLiveOpen}
                onConfirm={onLiveConfirmed}
                credsPresent={!!status?.creds_present}
                useTestnet={!!status?.use_testnet}
            />
            <ConfirmPaperDialog
                open={paperOpen}
                onOpenChange={setPaperOpen}
                onConfirm={onPaperConfirmed}
                isRunning={!!status?.running && status?.mode === "live"}
            />
            <ConfirmNetworkDialog
                open={networkOpen}
                onOpenChange={setNetworkOpen}
                onConfirm={onNetworkConfirmed}
                targetNetwork={networkTarget}
            />
            <GridDetailDialog
                open={!!gridDetailId}
                onOpenChange={(o) => { if (!o) setGridDetailId(null); }}
                gridId={gridDetailId}
            />
            <SettingsDialog
                open={settingsOpen}
                onOpenChange={setSettingsOpen}
                initial={config || status?.config || {}}
                autoSizing={status?.auto_sizing || null}
                walletAllocation={status?.wallet_allocation || null}
                onSave={onSaveSettings}
                isRunning={!!status?.running}
                onAlertPrefsChange={(p) => { alertPrefsRef.current = p; }}
            />
        </div>
    );
}

function App() {
    // Tiny hash-based router so the docs page works without nginx changes.
    const [route, setRoute] = useState(window.location.hash || "");
    // auth: null = checking, false = unauthenticated, object = authed user
    const [user, setUser] = useState(null);

    useEffect(() => {
        const onHash = () => setRoute(window.location.hash || "");
        window.addEventListener("hashchange", onHash);
        return () => window.removeEventListener("hashchange", onHash);
    }, []);

    useEffect(() => {
        authMe().then(setUser).catch(() => setUser(false));
        const onUnauth = () => setUser(false);
        window.addEventListener("auth:unauthorized", onUnauth);
        return () => window.removeEventListener("auth:unauthorized", onUnauth);
    }, []);

    const onLogout = async () => {
        try { await authLogout(); } catch (_) { /* ignore */ }
        setUser(false);
        window.location.hash = "";
    };

    // Password reset is reachable WITHOUT being logged in.
    if (route.startsWith("#/reset")) {
        return <ResetPasswordPage onDone={() => { window.location.hash = ""; setUser(false); }} />;
    }

    if (user === null) {
        return (
            <div className="min-h-screen flex items-center justify-center" style={{ background: "#0B0F14", color: "#6F7D92" }}>
                <div className="text-sm tracking-wide" data-testid="auth-loading">Loading…</div>
            </div>
        );
    }
    if (!user) {
        return <LoginPage onAuthed={setUser} />;
    }

    if (route.startsWith("#/docs")) {
        return <DocsPage onBack={() => { window.location.hash = ""; }} />;
    }
    if (route.startsWith("#/smartgrid/docs")) {
        return <SmartGridDocsPage onBack={() => { window.location.hash = "#/smartgrid"; }} />;
    }
    if (route.startsWith("#/smartgrid/history")) {
        return (
            <HistoryPage
                api={SG_HISTORY_API}
                title="SmartGrid — Trade History & Stats"
                backLabel="SmartGrid"
                onBack={() => { window.location.hash = "#/smartgrid"; }}
            />
        );
    }
    if (route.startsWith("#/smartgrid/group-sell")) {
        return (
            <GroupSellPage
                api={SG_GROUPSELL_API}
                title="SmartGrid — Group Sell & Orphan Manager"
                backLabel="SmartGrid"
                onBack={() => { window.location.hash = "#/smartgrid"; }}
            />
        );
    }
    if (route.startsWith("#/smartgrid/data-lab")) {
        return <DataLabPage onBack={() => { window.location.hash = "#/smartgrid"; }} />;
    }
    if (route.startsWith("#/smartgrid/blueprint")) {
        return <SmartGridBlueprintPage onBack={() => { window.location.hash = "#/smartgrid"; }} />;
    }
    if (route.startsWith("#/smartgrid/architecture")) {
        return <SmartGridArchitecturePage onBack={() => { window.location.hash = "#/smartgrid"; }} />;
    }
    if (route.startsWith("#/smartgrid/map")) {
        return <SmartGridSystemMapPage onBack={() => { window.location.hash = "#/smartgrid"; }} />;
    }
    if (route.startsWith("#/smartgrid")) {
        return <SmartGridPage onBack={() => { window.location.hash = ""; }} onLogout={onLogout} />;
    }
    if (route.startsWith("#/grids/")) {
        const gid = route.slice("#/grids/".length).split(/[/?#]/)[0];
        if (gid) {
            return <GridDashboardPage gid={gid} onBack={() => { window.location.hash = "#/grids"; }} />;
        }
    }
    if (route.startsWith("#/grids")) {
        return <MultiGridPage onBack={() => { window.location.hash = ""; }} />;
    }
    if (route.startsWith("#/server")) {
        return <ServerLoadPage onBack={() => { window.location.hash = ""; }} />;
    }
    if (route.startsWith("#/history")) {
        return <HistoryPage onBack={() => { window.location.hash = ""; }} />;
    }
    if (route.startsWith("#/group-sell")) {
        return <GroupSellPage onBack={() => { window.location.hash = ""; }} />;
    }
    return <Dashboard onLogout={onLogout} />;
}

export default App;
